import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import ImportExportButtons from "@/components/ImportExportButtons";
import BusinessCardScanner from "@/components/BusinessCardScanner";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { Plus, MagnifyingGlass, AddressBook, ScanSmiley, Sparkle, Trash } from "@phosphor-icons/react";

const TYPES = ["employee","manufacturer","supplier","jobworker","shipping_vendor","packaging_vendor","designer","photographer","model","influencer","marketing_agency","technology_vendor","customer","wholesale_buyer","legal_consultant","accountant","government","other"];

export default function Contacts() {
  const [rows, setRows] = useState([]);
  const [ctype, setCtype] = useState("");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", contact_type: "supplier", department: "vendors", phone: "", email: "", city: "", state: "", gstin: "" });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (ctype) params.set("contact_type", ctype);
      if (q) params.set("q", q);
      const { data } = await api.get(`/contacts?${params}`);
      setRows(data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ctype, q]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/contacts", form); toast.success("Contact added"); setShowNew(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const aiFill = async () => {
    if (!form.name && !form.company) { toast.info("Type a name or company as a hint first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/autofill", {
        entity: "contact",
        fields: ["designation", "department", "city", "state", "country", "contact_type"],
        hint: `${form.name} ${form.company}`,
      });
      const merged = { ...form };
      Object.entries(data.fields || {}).forEach(([k, v]) => { if (v && !merged[k]) merged[k] = v; });
      setForm(merged);
      toast.success("AI filled empty fields");
    } catch { toast.error("AI autofill failed"); }
    setAiBusy(false);
  };

  const deleteContact = async (c) => {
    try {
      const { data: deps } = await api.get(`/contacts/${c._id}/dependencies`);
      const detail = Object.entries(deps.counts).filter(([, v]) => v > 0).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join("\n");
      const msg = `Delete contact ${c.name}?\n\n` +
        (deps.total > 0
          ? `⚠️ This contact is linked to:\n${detail}\n\nDeleting will keep those records intact but soft-delete the contact (only super_admin/admin).\n\nProceed?`
          : "No linked records. This will permanently delete the contact.\n\nProceed?");
      if (!window.confirm(msg)) return;
      const force = deps.total > 0 ? "?force=true" : "";
      await api.delete(`/contacts/${c._id}${force}`);
      toast.success(deps.total > 0 ? "Contact soft-deleted (historic references kept)" : "Contact deleted");
      load();
    } catch (e) {
      const err = e?.response?.data;
      if (err?.dependencies) {
        toast.error(`Cannot delete — ${err.total || "several"} linked records exist. Ask a super_admin to force-delete.`);
      } else {
        toast.error(err?.detail || "Delete failed");
      }
    }
  };

  return (
    <div data-testid="contacts-page" className="page-fade">
      <PageHeader
        eyebrow="Module"
        title="Contacts & directory"
        description="Manufacturers, suppliers, jobworkers, vendors, employees and legal contacts — one source of truth. Scan a business card to add instantly."
        tint="indigo"
        icon={AddressBook}
        actions={
          <>
            <ImportExportButtons entity="contacts" onImported={load}/>
            <button className="btn-ghost" onClick={()=>setShowCard(true)} data-testid="contacts-scan-btn"><ScanSmiley size={14}/> Scan card</button>
            <button className="btn-primary" onClick={()=>setShowNew(true)} data-testid="new-contact-btn"><Plus size={14}/> New contact</button>
          </>
        }
      />
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search by name, company, phone…" className="input-soft pl-9" data-testid="contact-search"/>
        </div>
        <select value={ctype} onChange={(e)=>setCtype(e.target.value)} className="input-soft max-w-[220px]" data-testid="contact-type-filter">
          <option value="">All types</option>
          {TYPES.map(t=><option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
        </select>
      </div>
      <div className="card-soft p-0 overflow-x-auto">
        <table className="table-modern">
          <thead><tr><th>Name</th><th>Company</th><th>Type</th><th>City</th><th>Phone</th><th>Email</th><th>GSTIN</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} data-testid={`contact-row-${r._id}`}>
                <td><div className="font-display text-[15px] font-semibold leading-snug">{r.name}</div>{r.designation && <div className="text-xs text-muted-foreground">{r.designation}</div>}</td>
                <td className="text-sm">{r.company || <span className="text-muted-foreground">—</span>}</td>
                <td><span className="status-chip chip-taupe capitalize">{(r.contact_type||"").replace(/_/g," ")}</span></td>
                <td className="text-sm">{r.city || <span className="text-muted-foreground">—</span>}</td>
                <td className="font-mono text-xs">{r.phone || "—"}</td>
                <td className="text-sm text-muted-foreground">{r.email || "—"}</td>
                <td className="font-mono text-xs">{r.gstin || "—"}</td>
                <td className="text-right">
                  <RecordActions entity="contacts" record={r} canDelete={false} onChanged={load} />
                  <button onClick={()=>deleteContact(r)} className="btn-ghost btn-icon text-destructive" data-testid={`contact-delete-${r._id}`} title="Delete contact"><Trash size={14}/></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8}><EmptyState icon={AddressBook} title="No contacts yet" description="Add your first vendor, employee or partner — or scan a business card to import instantly." /></td></tr>}
          </tbody>
        </table>
      </div>

      {showCard && (
        <BusinessCardScanner onSaved={() => load()} onClose={() => setShowCard(false)} />
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setShowNew(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-contact-form">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow">New contact</div>
                <div className="display-3 mt-1">Create</div>
              </div>
              <button type="button" onClick={aiFill} disabled={aiBusy} className="btn-ghost btn-sm" data-testid="contact-ai-autofill" title="Fill blank fields with AI"><Sparkle size={12}/> {aiBusy ? "Filling…" : "AI autofill"}</button>
            </div>
            <div className="section-card">
              <div className="section-title mb-4">Identity</div>
              <div className="grid grid-cols-2 gap-4">
                {[["name","Full name"],["company","Company"],["designation","Designation"]].map(([k,l])=>(
                  <div className={`float-field ${form[k]?"has-value":""} ${k==="designation"?"col-span-2":""}`} key={k}>
                    <input value={form[k]||""} onChange={(e)=>setForm({...form,[k]:e.target.value})} placeholder=" " data-testid={`contact-input-${k}`} required={k==="name"}/>
                    <label>{l}</label>
                  </div>
                ))}
                <div className="float-field is-select">
                  <select value={form.contact_type} onChange={(e)=>setForm({...form, contact_type: e.target.value})} data-testid="contact-input-type">
                    {TYPES.map(t=><option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
                  </select>
                  <label>Type</label>
                </div>
              </div>
            </div>
            <div className="section-card">
              <div className="section-title mb-4">Reach</div>
              <div className="grid grid-cols-2 gap-4">
                {[["phone","Phone"],["email","Email"],["city","City"],["state","State"],["gstin","GSTIN"]].map(([k,l])=>(
                  <div className={`float-field ${form[k]?"has-value":""}`} key={k}>
                    <input value={form[k]||""} onChange={(e)=>setForm({...form,[k]:e.target.value})} placeholder=" " data-testid={`contact-input-${k}`}/>
                    <label>{l}</label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button type="button" onClick={()=>setShowNew(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="contact-submit">Add contact</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
